import React from 'react';
import { Card, Form, Input, Button, Select, Typography, Space, Flex } from 'antd';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Title } = Typography;

const NewEntryForm = ({ onSubmit, editingEntry, loading, formRef, onCancel }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    React.useEffect(() => {
        if (editingEntry) {
            form.setFieldsValue({
                entryType: editingEntry.entry_type === 'question_answer' ? 'qa' : 'business',
                question: editingEntry.data.question,
                answer: editingEntry.data.answer,
                content: editingEntry.data.content
            });
        }
    }, [editingEntry, form]);

    const handleSubmit = async (values) => {
        await onSubmit(values);
        form.resetFields();
    };

    return (
        <Card
            style={{
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
            }}
            title={
                <Title level={3} style={{ margin: 0 }}>
                    {editingEntry ? t('edit_entry') : t('add_new_entry')}
                </Title>
            }
            ref={formRef}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ entryType: 'qa' }}
                style={{ marginBottom: 0 }}
            >
                <Space direction="vertical" style={{ width: '100%', gap: 8 }} size="large">
                    <Form.Item
                        name="entryType"
                        label={t('entry_type')}
                        rules={[{ required: true, message: t('please_select_entry_type') }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Select>
                            <Select.Option value="qa">{t('qa_entry')}</Select.Option>
                            <Select.Option value="business">{t('business_info')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        style={{ marginBottom: 0 }}
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.entryType !== currentValues.entryType
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('entryType') === 'qa' ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Form.Item
                                        name="question"
                                        label={t('question')}
                                        style={{ marginBottom: 0 }}
                                        rules={[{ required: true, message: t('please_enter_question') }]}
                                    >
                                        <TextArea
                                            autoSize={{ minRows: 2 }}
                                            placeholder={t('enter_question_here')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="answer"
                                        label={t('answer')}
                                        rules={[{ required: true, message: t('please_enter_answer') }]}
                                    >
                                        <TextArea
                                            autoSize={{ minRows: 3 }}
                                            placeholder={t('enter_answer_here')}
                                        />
                                    </Form.Item>
                                </Space>
                            ) : (
                                <Form.Item
                                    name="content"
                                    label={t('business_information')}
                                    rules={[{ required: true, message: t('please_enter_business_information') }]}
                                >
                                    <TextArea
                                        autoSize={{ minRows: 4 }}
                                        placeholder={t('enter_business_information_here')}
                                    />
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    <Form.Item noStyle>
                        <Flex gap={8} wrap>
                            {editingEntry && <Button type="default" onClick={onCancel} block>
                                {t('cancel')}
                            </Button>}
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                            >
                                {editingEntry ? t('update_entry') : t('add_entry')}
                            </Button>
                        </Flex>
                    </Form.Item>
                </Space>
            </Form>
        </Card>
    );
};

export default NewEntryForm;
